import { Box, Card, Container, Tab, Tabs } from '@mui/material';
import type { FC, ReactNode } from 'react';

import { fmAppMainPages, generatePath } from '@pflegenavi/frontend/routing';
import {
  HeaderBreadcrumbs,
  ProfileIcon,
  SvgIconStyle,
  TabsWrapperStyle,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { ProfileCover } from './ProfileCover';

interface ProfilePageViewProps {
  profileText: string;
  overviewText: string;
  currentTab?: string;
  onTabClick?: (tabKey: string) => void;
  profileCoverContent?: ReactNode;
  outletContent?: ReactNode;
}

export const ProfilePageView: FC<ProfilePageViewProps> = ({
  profileText,
  overviewText,
  currentTab = 'details',
  onTabClick,
  profileCoverContent,
  outletContent,
}) => {
  const PROFILE_TABS = [
    {
      key: 'details',
      value: profileText,
      icon: <SvgIconStyle src={ProfileIcon} sx={{ width: 20, mr: 1 }} />,
      onClick: () => {
        onTabClick?.('details');
      },
    },
  ];

  return (
    <Box>
      <Container maxWidth={'lg'}>
        <HeaderBreadcrumbs
          heading={profileText}
          links={[
            { name: profileText, href: fmAppMainPages.PROFILE },
            { name: overviewText },
          ]}
        />
        <Card
          sx={{
            mb: 4,
            height: 200,
            position: 'relative',
          }}
        >
          {profileCoverContent || <ProfileCover />}

          <TabsWrapperStyle>
            <Tabs
              allowScrollButtonsMobile
              scrollButtons="auto"
              value={currentTab}
            >
              {PROFILE_TABS.map((tab) => (
                <Tab
                  onClick={tab.onClick}
                  disableRipple
                  sx={{ fontSize: 16 }}
                  key={tab.key}
                  value={tab.key}
                  icon={tab.icon}
                  label={tab.value}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </TabsWrapperStyle>
        </Card>
        {outletContent || <Outlet />}
      </Container>
    </Box>
  );
};

export const ProfilePage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleTabClick = (tabKey: string) => {
    navigate(generatePath(fmAppMainPages.PROFILE));
  };

  return (
    <ProfilePageView
      profileText={t('usermenu.profile')}
      overviewText={t('common.overview')}
      onTabClick={handleTabClick}
    />
  );
};
